import React from 'react';
import { Button } from './ui/button';
import { PlayCircle, FileEdit, Sparkles, TrendingUp, ShieldCheck, Save, RefreshCw, Layers, ArrowRight } from 'lucide-react';

const YT_TUTORIAL = 'https://youtu.be/Fe69dUXnXuQ';

export default function BorradoresEmptyState({ onCreate }) {
    return (
        <div className="mx-auto max-w-4xl" data-testid="borradores-empty-state">
            {/* Hero */}
            <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-indigo-50/40 to-white p-6 sm:p-10 shadow-sm">
                <div className="text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-indigo-700">
                        <Sparkles className="h-3 w-3" />
                        Tu espacio de trabajo
                    </div>
                    <h1 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                        👋 ¡Comencemos a construir tu Portafolio Docente 2026!
                    </h1>
                    <p className="mt-4 text-slate-700 leading-relaxed max-w-2xl mx-auto text-sm sm:text-base">
                        Este será tu espacio de trabajo durante todo el proceso. Aquí podrás crear tus borradores,
                        analizarlos con Inteligencia Artificial especializada, recibir diagnósticos pedagógicos y
                        mejorarlos todas las veces que necesites.
                    </p>
                    <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl mx-auto text-sm">
                        No te preocupes si aún no tienes nada escrito. Puedes comenzar con una primera versión
                        sencilla y luego ir perfeccionándola.
                    </p>

                    {/* CTA principal */}
                    <div className="mt-8">
                        <Button
                            size="lg"
                            onClick={onCreate}
                            className="h-14 px-8 text-base font-bold bg-indigo-700 hover:bg-indigo-800 shadow-lg shadow-indigo-200 gap-3 rounded-xl transition-transform hover:-translate-y-0.5"
                            data-testid="empty-state-create-btn"
                        >
                            <FileEdit className="h-5 w-5" />
                            Crear mi primer borrador
                        </Button>
                    </div>

                    {/* Botón secundario tutorial */}
                    <div className="mt-4">
                        <a href={YT_TUTORIAL} target="_blank" rel="noopener noreferrer">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="gap-2 text-slate-600 hover:text-indigo-700"
                                data-testid="empty-state-tutorial-btn"
                            >
                                <PlayCircle className="h-4 w-4" />
                                Ver tutorial (2 minutos)
                            </Button>
                        </a>
                    </div>
                </div>

                {/* 3 Pasos con línea de progreso */}
                <div className="mt-10 pt-8 border-t border-slate-100">
                    <p className="text-center text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-6">
                        ¿Cómo funciona?
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-2 relative">
                        {/* Línea horizontal de progreso (desktop) */}
                        <div className="hidden sm:block absolute top-8 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-indigo-200 via-indigo-300 to-emerald-200" aria-hidden="true" />

                        {[
                            { icon: FileEdit, label: 'Crear un borrador', desc: 'Elige tarea, indicador y comienza a escribir.', color: 'indigo' },
                            { icon: Sparkles, label: 'Analizar con IA especializada', desc: 'La IA revisará tu texto según la rúbrica CPEIP.', color: 'indigo' },
                            { icon: TrendingUp, label: 'Recibir sugerencias para mejorar', desc: 'Iteras cuantas veces necesites hasta perfeccionarlo.', color: 'emerald' },
                        ].map((s, i) => (
                            <div key={i} className="relative flex flex-col items-center text-center" data-testid={`step-${i + 1}`}>
                                <div className={`relative z-10 flex h-16 w-16 items-center justify-center rounded-full border-4 border-white shadow-md ${s.color === 'emerald' ? 'bg-emerald-100' : 'bg-indigo-100'}`}>
                                    <s.icon className={`h-7 w-7 ${s.color === 'emerald' ? 'text-emerald-700' : 'text-indigo-700'}`} />
                                </div>
                                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                                    Paso {i + 1}
                                </div>
                                <p className="mt-2 font-heading text-sm font-bold text-slate-900">{s.label}</p>
                                <p className="mt-1 text-xs text-slate-500 leading-snug max-w-[220px]">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tarjeta verde de tranquilidad */}
            <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50/70 p-5 sm:p-6" data-testid="tranquility-card">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-heading text-base font-bold text-emerald-900">¿Sabías que...?</h3>
                        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                            {[
                                { icon: RefreshCw, text: 'Puedes modificar tu borrador todas las veces que quieras.' },
                                { icon: ShieldCheck, text: 'Nada se envía al CPEIP desde esta plataforma.' },
                                { icon: Save, text: 'Todo tu trabajo queda guardado automáticamente.' },
                                { icon: Layers, text: 'Puedes probar distintas versiones antes de decidir cuál conservar.' },
                            ].map((it, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-emerald-900/90">
                                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-700">
                                        <it.icon className="h-3 w-3" />
                                    </span>
                                    <span className="leading-relaxed">{it.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* CTA repetido inferior */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                    size="lg"
                    onClick={onCreate}
                    className="h-12 px-6 gap-2 bg-indigo-700 hover:bg-indigo-800"
                    data-testid="empty-state-create-btn-bottom"
                >
                    <FileEdit className="h-4 w-4" />
                    Crear mi primer borrador
                    <ArrowRight className="h-4 w-4" />
                </Button>
                <a href={YT_TUTORIAL} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="lg" className="h-12 px-5 gap-2">
                        <PlayCircle className="h-4 w-4" />
                        Ver tutorial
                    </Button>
                </a>
            </div>
        </div>
    );
}
